// ---------------------------------------------------------------------------
// KgValidateTab — DevTools surface for `repo.validate(phase)`.
//
// Phase 2b §S6 per docs/studio/README.md v1.6 §6.4.1 + kg-repository-usage.md
// §2.2. Whole-store SHACL validation today (per-node validation is blocked
// on agentic common A1 `loadJsonLd` + `dropGraph`). Click-to-run UX — no
// auto-validation on mount to avoid surprising the user.
//
// Violations are shown grouped by severity (violation / warning / info).
// Reuses `ValidationBadge` for the top summary so the visual language
// matches the existing 14-stage validator panel.
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useState } from 'react'
import { useKg } from '@/features/design-system/kg-provider'
import { useStudioStore } from '@/store/studio-store'
import { usePreviewTestingStore } from '@/features/preview/store/preview-testing-store'
import {
  runLocalValidation,
  type LocalValidationResult,
} from '@/features/validation/local-validator'
import { ValidationBadge } from '@/features/validation/components/validation-badge'
import { FallbackPromotionControlTowerPanel } from '@/features/validation/components/fallback-promotion-control-tower-panel'
import {
  buildFallbackPromotionControlTowerModelFromActionFlowEvidence,
  collectFallbackPromotionControlTowerModels,
} from '@/features/validation/fallback-promotion-control-tower-model'
import { buildActionFlowJsonEvidence } from '@/features/json-editor/lib/actionflow-json-evidence'
import type { ValidationPhase } from '@cogo/kg-repository'
import type { ShaclViolation } from '@cogo/kg-browser'

const SEVERITY_COLOR: Record<string, string> = {
  violation: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-sky-600',
}

function summarize(
  result: LocalValidationResult | null,
): { pass: number; warn: number; fail: number } {
  if (!result) return { pass: 0, warn: 0, fail: 0 }
  let warn = 0
  let fail = 0
  let info = 0
  for (const v of result.violations) {
    if (v.severity === 'violation') fail += 1
    else if (v.severity === 'warning') warn += 1
    else if (v.severity === 'info') info += 1
  }
  const evaluatedButPassed = Math.max(
    0,
    result.totalRules - result.violations.length,
  )
  return { pass: evaluatedButPassed + info, warn, fail }
}

export function KgValidateTab() {
  const { repo, ready, flagEnabled } = useKg()
  const [phase, setPhase] = useState<ValidationPhase>('structural')
  const [result, setResult] = useState<LocalValidationResult | null>(null)
  const [running, setRunning] = useState(false)

  // Fallback-promotion control tower — operator/ontology-curation surface.
  // This DevTools tab is its designated home (it was previously mounted on
  // the creator-facing Code surface and block inspector).
  const currentProject = useStudioStore((s) => s.currentProject)
  const latestActionFlowTrace = usePreviewTestingStore((s) => s.actionFlowTraces[0])
  const controlTowerModels = useMemo(() => {
    const projectJson = (currentProject ?? {}) as unknown as Record<string, unknown>
    const attachedModels = collectFallbackPromotionControlTowerModels(projectJson)
    const runtimeEvidence = latestActionFlowTrace?.status === 'pass'
      ? buildActionFlowJsonEvidence(projectJson, latestActionFlowTrace)
      : []
    const runtimeModels = buildFallbackPromotionControlTowerModelFromActionFlowEvidence(
      runtimeEvidence,
      {
        scope: latestActionFlowTrace
          ? `Preview ActionFlow: ${latestActionFlowTrace.eventLabel}`
          : 'Preview ActionFlow runtime',
        actionFlowName: latestActionFlowTrace?.flowName,
      },
    )
    return [...attachedModels, ...runtimeModels]
  }, [currentProject, latestActionFlowTrace])

  const runValidation = useCallback(async () => {
    setRunning(true)
    try {
      const out = await runLocalValidation(repo, phase)
      setResult(out)
    } finally {
      setRunning(false)
    }
  }, [repo, phase])

  const counts = summarize(result)
  const canRun = Boolean(repo) && ready && flagEnabled && !running

  return (
    <div className="flex flex-col h-full text-xs">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-3 py-2 shrink-0">
        <button
          onClick={runValidation}
          disabled={!canRun}
          className="px-2 py-1 text-[11px] rounded border border-primary bg-primary/10 text-primary hover:bg-primary/15 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
          title={
            !flagEnabled
              ? 'VITE_KG_BROWSER flag is off'
              : !ready
                ? 'KG is not ready yet'
                : !repo
                  ? 'Repository unavailable'
                  : 'Run SHACL validation against the current KG store'
          }
        >
          {running ? 'Running…' : 'Run'}
        </button>

        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          phase:
          {(['structural', 'binding'] as ValidationPhase[]).map((p) => (
            <label key={p} className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="kg-validate-phase"
                checked={phase === p}
                onChange={() => setPhase(p)}
                className="accent-current"
              />
              <span className={phase === p ? 'text-foreground font-medium' : ''}>
                {p}
              </span>
            </label>
          ))}
        </div>

        <div className="flex-1" />

        {result ? (
          <div className="flex items-center gap-2">
            <ValidationBadge
              counts={counts}
              status={result.status}
              label={`${result.totalRules} rules`}
            />
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {result.durationMs.toFixed(2)}ms
              {' · '}
              {result.fromLocal ? 'local' : 'skipped'}
            </span>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">
            no run yet
          </span>
        )}
      </div>

      <FallbackPromotionControlTowerPanel
        models={controlTowerModels}
        liveObservationUrl="/kg/observations"
      />

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {!flagEnabled ? (
          <div className="p-6 text-center text-muted-foreground text-[11px]">
            KG Browser flag is off. Enable <code>VITE_KG_BROWSER=1</code> to
            run validation.
          </div>
        ) : !result ? (
          <div className="p-6 text-center text-muted-foreground text-[11px]">
            Click <span className="font-medium text-foreground">Run</span> to
            execute SHACL{' '}
            <span className="font-mono">{phase}</span>-phase rules on the
            current KG store.
          </div>
        ) : result.error ? (
          <div className="m-3 p-3 rounded border border-red-500/25 bg-red-500/5 text-red-700 text-[11px]">
            <div className="font-medium mb-1">Validation failed</div>
            <div className="font-mono break-words">{result.error}</div>
          </div>
        ) : result.violations.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-[11px]">
            <span className="text-success font-medium">No violations.</span>{' '}
            {result.totalRules} rule{result.totalRules === 1 ? '' : 's'}{' '}
            evaluated.
          </div>
        ) : (
          <ViolationList violations={result.violations} />
        )}
      </div>
    </div>
  )
}

function ViolationList({ violations }: { violations: ShaclViolation[] }) {
  const [expanded, setExpanded] = useState<number | null>(null)

  return (
    <ul className="divide-y divide-border/50 font-mono text-[11px]">
      {violations.map((v, idx) => (
        <li key={idx} className="px-3 py-2">
          <button
            onClick={() => setExpanded(expanded === idx ? null : idx)}
            className="w-full flex items-start gap-2 text-left"
          >
            <span
              className={`shrink-0 text-[8px] leading-none mt-1 ${SEVERITY_COLOR[v.severity] ?? 'text-muted-foreground'}`}
            >
              ●
            </span>
            <span className="flex-1">
              <span className="text-foreground">{v.message}</span>
              <span className="ml-2 text-muted-foreground text-[10px]">
                ({v.shape}
                {v.focusClass ? ` · ${v.focusClass}` : ''})
              </span>
            </span>
            <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
              {v.severity}
            </span>
          </button>
          {expanded === idx && v.bindings && Object.keys(v.bindings).length > 0 ? (
            <div className="mt-2 ml-4 p-2 rounded bg-foreground/5 border border-border/50 text-[10px]">
              <div className="text-muted-foreground mb-1">
                bindings ({Object.keys(v.bindings).length})
              </div>
              <table className="w-full border-collapse">
                <tbody>
                  {Object.entries(v.bindings).map(([k, val]) => (
                    <tr key={k}>
                      <td className="pr-2 py-0.5 text-muted-foreground align-top whitespace-nowrap">
                        {k}
                      </td>
                      <td className="pr-2 py-0.5 break-all">
                        {String(val)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
